import { TSchedule } from '@/domain/Availability.domain';
import { Schedule } from './Schedule';
import { SchedulesSkeleton } from './SchedulesSkeleton';
import { useState } from 'react';
import { cn } from '@/service/utils/cn';
import { scrollToId, scrollToIdWithMargin } from '@/service/utils/scrollToId';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';
import { CallToBook } from '@/ui/components/AttractionsContent/core/CallToBook';
import { ArrowWithTailIcon } from '../Icons/ArrowWithTailIcon';
import { format } from 'date-fns';
import { OVERNIGHTS_DATE_FORMAT, SCHEDULE_DATE_FORMAT } from '@/service/constants/dates';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { isPastTime } from '@/service/utils/isPastTime';
import { useAvailabilityStore } from '@/service/state/Availability/useAvailabilityStore';
import { ScheduleText } from './ScheduleText';
import { ArrowLeftIcon } from '../Icons/ArrowLeftIcon';
import { ExchangeIcon } from '../Icons/ExchangeIcon';

type TSchedulesProps = {
    list?: TSchedule[];
    isLoading?: boolean;
    limit?: number;
    isUseSchedule?: boolean;
    isUseCalendar?: boolean;
    monthUnavail?: boolean;
    selected?: Date;
    notAvailDay?: boolean;
    hasOvernights?: boolean;
    selectSchedule: (scheduleId: number) => void;
    selectNextAvailDay?: () => void;
};

const OFFSET_TO_LAST_EL = -635;
const HEADER_MARGIN = 72;

export const Schedules = ({
    list,
    isLoading,
    limit,
    selectSchedule,
    isUseSchedule,
    isUseCalendar,
    selectNextAvailDay,
    monthUnavail,
    selected,
    notAvailDay,
    hasOvernights,
}: TSchedulesProps) => {
    const [showFullList, setShowFullList] = useState(false);
    const { isLightframePage, texts, phone, timezone } = usePartnerStore();
    const {
        overnightsView,
        overnightsDurations,
        selectedDate,
        days,
        toggleOvernights,
        selectedOvernightsDuration,
        setOvernightsDuration,
        loadingOvernights,
        changeDay,
    } = useAvailabilityStore();
    const overnightsKey = selectedDate + '-overnights';
    const overnightsDuration = overnightsView && !isLoading && overnightsDurations[overnightsKey];
    const showOvernightsTime = overnightsView && !!selectedOvernightsDuration;
    const overnightsList =
        showOvernightsTime &&
        days[overnightsKey]?.data?.[0]?.schedules.filter(({ durations }) =>
            durations?.includes(selectedOvernightsDuration)
        );

    const showLoader = isLoading || loadingOvernights;
    const listToShow =
        showLoader || !list
            ? []
            : (showOvernightsTime && !!overnightsList && overnightsList) ||
              list.filter(
                  ({ is_avail, call_to_booking, time }) =>
                      is_avail || (call_to_booking && !isPastTime(time, timezone))
              );

    const isLong = !showLoader && limit && listToShow.length > limit && !showFullList;
    const isUnavailable =
        (!showLoader && !listToShow.length && isUseSchedule) ||
        (notAvailDay && !isLoading && !loadingOvernights && !overnightsView && !listToShow.length);
    const isDateless = !isUseSchedule && !isUseCalendar;

    return (
        <div
            id="schedules"
            data-test-id="schedules"
            className={cn(
                'after-shadow relative mt-5 w-full rounded-2xl bg-white p-6 pb-3 shadow-lightCard after:bottom-[16px] dark:bg-dark-3 dark:shadow-darkCard md:px-3 md:pb-4 lg:px-4'
            )}
        >
            <div className="after-shadow tx-large-semibold relative mb-1 text-gray-2 after:bottom-[-20px] after:rotate-180 dark:text-gray-3">
                {!!hasOvernights && !overnightsView && !isUnavailable && (
                    <div className="mb-6 flex justify-end md:mb-4">
                        <button
                            className=" tx-heading-2 flex w-fit cursor-pointer items-center text-brand md:tx-large-semibold hover:underline"
                            type="button"
                            onClick={() => {
                                toggleOvernights();
                                scrollToIdWithMargin('schedules', { margin: HEADER_MARGIN });
                            }}
                        >
                            Book multiple days
                            <ArrowRightIcon className="ml-2 h-4 w-4 md:h-3 md:w-3 [&_path]:fill-brand" />
                        </button>
                    </div>
                )}
                {!!hasOvernights && overnightsView && !loadingOvernights && (
                    <div
                        className={cn(
                            'mb-6 flex justify-end md:mb-4',
                            selectedOvernightsDuration && overnightsDuration && 'justify-between'
                        )}
                    >
                        {selectedOvernightsDuration && overnightsDuration && (
                            <button
                                type="button"
                                onClick={() => {
                                    setOvernightsDuration('');
                                    scrollToIdWithMargin('schedules', { margin: HEADER_MARGIN });
                                }}
                                className="flex items-center hover:underline"
                            >
                                <ArrowLeftIcon className="mr-1 h-3 w-3 [&_path]:fill-gray-2 [&_path]:dark:fill-gray-3" />
                                Back to options
                            </button>
                        )}
                        <button
                            className="tx-large-semibold flex w-fit cursor-pointer items-center text-gray-3 hover:underline dark:text-gray-4"
                            type="button"
                            onClick={() => {
                                toggleOvernights();
                                scrollToIdWithMargin('schedules', { margin: HEADER_MARGIN });
                            }}
                        >
                            Switch to hourly
                            <ExchangeIcon className="ml-1 h-4 w-4 [&_path]:fill-gray-3 [&_path]:dark:fill-gray-4" />
                        </button>
                    </div>
                )}
                {(!overnightsView || !!selectedOvernightsDuration) && (
                    <div className="flex justify-between">
                        {overnightsView
                            ? 'Select Pick Up Time'
                            : !showLoader && !listToShow.length && isUseSchedule
                              ? 'Online booking unavailable'
                              : `${
                                    isUseSchedule
                                        ? texts?.['Select Trip Time'] || 'Select Trip Time'
                                        : (isUseCalendar && 'All Day Availability') || 'Book Online'
                                }`}
                        {!showLoader && !!selected && !isDateless && (
                            <p>{format(selected, SCHEDULE_DATE_FORMAT)}</p>
                        )}
                    </div>
                )}
                {overnightsView && !selectedOvernightsDuration && (
                    <div className="flex justify-between">
                        <button
                            className={cn(
                                'tx-medium-semibold flex items-center text-gray-3 hover:underline'
                                /* !listToShow.length && 'invisible' */
                            )}
                            type="button"
                            onClick={() => changeDay('prev')}
                        >
                            <ArrowLeftIcon className="mr-1 h-3 w-3 [&_path]:fill-gray-3" />
                            Prev day
                        </button>
                        {!!selected && format(selected, OVERNIGHTS_DATE_FORMAT)}
                        <button
                            className={cn(
                                'tx-medium-semibold flex items-center text-gray-3 hover:underline'
                                /* !listToShow.length && 'invisible' */
                            )}
                            type="button"
                            onClick={() => changeDay('next')}
                        >
                            Next day
                            <ArrowRightIcon className="ml-1 h-3 w-3 [&_path]:fill-gray-3" />
                        </button>
                    </div>
                )}
            </div>
            {isUnavailable && (
                <>
                    <p
                        className={cn(
                            'tx-large-semibold relative mx-auto mt-3 w-fit min-w-[210px] text-brand md:min-w-[240px]',
                            monthUnavail ? 'flex justify-center' : 'cursor-pointer underline'
                        )}
                        onClick={selectNextAvailDay}
                    >
                        {monthUnavail ? 'Please select another date' : 'Go to next available date'}
                        {!monthUnavail && (
                            <ArrowWithTailIcon className="absolute bottom-0 right-0 h-5 w-5 md:bottom-auto md:top-0" />
                        )}
                    </p>
                    <p className="tx-large-medium mx-auto mt-2 w-fit text-gray-3 dark:text-dark-1">
                        Or
                    </p>
                    <CallToBook className="mx-auto mt-2 flex w-[210px] px-3 md:w-[240px]" />
                </>
            )}
            <ul
                className={cn(
                    'custom-scroll relative flex w-full flex-col overflow-y-auto overflow-x-hidden pt-4 duration-1000 md:max-h-[392px] md:pr-3',
                    isLong
                        ? 'animate-viewMore max-h-[782px] overflow-y-hidden  md:max-h-[392px] md:overflow-y-auto'
                        : ''
                )}
            >
                {!isUnavailable && overnightsView && !selectedOvernightsDuration && (
                    <>
                        {loadingOvernights ? (
                            <SchedulesSkeleton />
                        ) : (
                            Object.entries(overnightsDuration).map(([value, text]) => (
                                <ScheduleText
                                    value={value}
                                    content={text}
                                    handler={(duration) => {
                                        setOvernightsDuration(duration);
                                        scrollToIdWithMargin('schedules', {
                                            margin: HEADER_MARGIN,
                                        });
                                    }}
                                    key={value}
                                />
                            ))
                        )}
                    </>
                )}
                {!isUnavailable && (!overnightsView || !!selectedOvernightsDuration) && (
                    <>
                        {showLoader ? (
                            <SchedulesSkeleton />
                        ) : listToShow.length ? (
                            listToShow.map(
                                ({
                                    schedule_id,
                                    time,
                                    tickets,
                                    price,
                                    min_tickets,
                                    description,
                                    call_to_booking,
                                }) => (
                                    <Schedule
                                        id={schedule_id}
                                        key={schedule_id}
                                        time={time}
                                        ticketsAvail={tickets.avail}
                                        price={price}
                                        minTickets={min_tickets}
                                        description={description}
                                        callToBooking={call_to_booking}
                                        phone={phone}
                                        handler={() => {
                                            selectSchedule(schedule_id);
                                        }}
                                    />
                                )
                            )
                        ) : (
                            <div>
                                {(isUseSchedule && (
                                    <div className="mb-9 mt-12 flex min-h-[200px] w-full items-center justify-center bg-no-activities bg-contain bg-center bg-no-repeat dark:bg-no-activities-dark" />
                                )) || (
                                    <div
                                        className="tx-large-semibold mb-4 flex h-[65px] w-full cursor-pointer items-center justify-between gap-x-1 rounded-2xl border border-gray-2 pl-6 pr-3 text-gray-2 dark:border-gray-3 dark:text-gray-3"
                                        onClick={() => {
                                            selectSchedule(0);
                                        }}
                                    >
                                        Select Tickets
                                        <div className="flex h-[34px] w-[35px] items-center justify-center">
                                            <ArrowRightIcon className="h-[50%] w-[50%] [&_path]:fill-gray-3" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {isLong && !!listToShow.length && (
                            <li
                                className="tx-heading-3 absolute bottom-2 flex h-[65px] w-full cursor-pointer items-center justify-center gap-x-1 overflow-hidden rounded-2xl border border-gray-2 bg-white pl-6 pr-3 text-brand underline dark:border-gray-3 dark:bg-dark-3 md:hidden"
                                onClick={() => {
                                    setTimeout(() => setShowFullList(true), 100);
                                    isLightframePage
                                        ? scrollToId('schedules', 'end')
                                        : scrollToIdWithMargin('schedules', {
                                              margin: OFFSET_TO_LAST_EL,
                                          });
                                }}
                            >
                                Show More
                            </li>
                        )}
                    </>
                )}
            </ul>
        </div>
    );
};
